import { Plus } from 'lucide-react';

import {
    AdminBadge,
    AdminStatusDot,
} from '@apps/web/features/admin_design/admin_badge';
import { AdminButton } from '@apps/web/features/admin_design/admin_controls';
import { AdminCard } from '@apps/web/features/admin_design/admin_shell';
import { cn } from '@apps/web/lib/utils';

/*
 * Foundations data + presentational sub-components extracted from
 * design-kit.tsx so the route file stays under the ~1k line ceiling
 * the maintainability contract calls for. Every palette constant,
 * type/radius/motion row, the shared SwatchRow/SwatchColumn helpers,
 * the stagger prop helper, and the PhoebePurpleStory poster live
 * here. The route file consumes these directly.
 */

export type Swatch = {
    readonly name: string;
    readonly variable: string;
    readonly value: string;
    readonly note?: string;
    /**
     * Optional foreground demo. When set, the swatch preview square
     * paints `textOn` as background with `value` as the readable ink
     * and renders an "Aa" sample so text-on-color entries prove they
     * are legible in situ (e.g. ink-on-purple).
     */
    readonly textOn?: string;
};

export const PURPLE_SWATCHES: ReadonlyArray<Swatch> = [
    {
        name: 'purple-wash',
        variable: '--admin-purple-wash',
        value: '#f7f5fe',
        note: 'row wash, subtle grounds',
    },
    {
        name: 'purple-soft',
        variable: '--admin-purple-soft',
        value: '#eeeafd',
        note: 'selection highlight, chip surfaces',
    },
    {
        name: 'purple-border',
        variable: '--admin-purple-border',
        value: '#d8cffb',
        note: 'card hover hairline',
    },
    {
        name: 'purple',
        variable: '--admin-purple',
        value: '#5533ee',
        note: 'primary CTA, focus ring, live dot, tab underline',
    },
    {
        name: 'purple-strong',
        variable: '--admin-purple-strong',
        value: '#4629c7',
        note: 'primary hover, active',
    },
    {
        // Accessible white ink for solid Phoebe purple fills — the
        // pair that primary CTAs, solid ink-primary badges, and the
        // active-tab-underline callouts render against. Included in
        // the labeled scale so a reviewer sees the readable pairing
        // alongside the underlying purple stops, not as an implicit.
        name: 'ink-on-purple',
        variable: '--admin-purple-ink',
        value: '#ffffff',
        note: 'white ink on --admin-purple; WCAG AA on the base stop',
        textOn: '#5533ee',
    },
];

export const GROUND_SWATCHES: ReadonlyArray<Swatch> = [
    {
        name: 'canvas',
        variable: '--admin-canvas',
        value: '#faf9fb',
        note: 'page background — off-white with a whisper of lilac',
    },
    {
        name: 'surface',
        variable: '--admin-surface',
        value: '#ffffff',
        note: 'cards',
    },
    {
        name: 'surface-hover',
        variable: '--admin-surface-hover',
        value: '#efecf3',
        note: 'row hover surface',
    },
    {
        name: 'sunken',
        variable: '--admin-sunken',
        value: '#f5f3f7',
        note: 'skeleton base, nested surfaces',
    },
];

export const INK_SWATCHES: ReadonlyArray<Swatch> = [
    {
        name: 'ink',
        variable: '--admin-ink',
        value: '#14121a',
        note: 'near-black with a slight purple cast',
    },
    {
        name: 'ink-secondary',
        variable: '--admin-ink-secondary',
        value: '#4d4a58',
    },
    { name: 'ink-muted', variable: '--admin-ink-muted', value: '#6b6874' },
    { name: 'ink-faint', variable: '--admin-ink-faint', value: '#a09ca8' },
];

export const BORDER_SWATCHES: ReadonlyArray<Swatch> = [
    { name: 'border', variable: '--admin-border', value: '#e6e2ec' },
    {
        name: 'border-strong',
        variable: '--admin-border-strong',
        value: '#d0cbd8',
    },
    {
        name: 'border-focus',
        variable: '--admin-border-focus',
        value: '#5533ee',
        note: 'input focus (Phoebe purple)',
    },
];

export const STATUS_SWATCHES: ReadonlyArray<Swatch> = [
    { name: 'ok-text', variable: '--admin-ok-text', value: '#1f7a3a' },
    { name: 'warn-text', variable: '--admin-warn-text', value: '#a06a0a' },
    { name: 'danger-text', variable: '--admin-danger-text', value: '#b91c1c' },
    { name: 'info-text', variable: '--admin-info-text', value: '#2f5aa8' },
];

export type TypeRow = {
    readonly label: string;
    readonly variable: string;
    readonly className: string;
    readonly sample: string;
};

export const TYPE_ROWS: ReadonlyArray<TypeRow> = [
    {
        label: 'micro-caps · 11px · +6% tracking',
        variable: '--text-admin-xs',
        className:
            'text-admin-xs text-admin-ink-muted font-medium tracking-[0.06em] uppercase',
        sample: 'Section label',
    },
    {
        label: 'meta · 12px',
        variable: '--text-admin-sm',
        className: 'text-admin-sm text-admin-ink-muted',
        sample: 'Updated 32 minutes ago · 128 total',
    },
    {
        label: 'body · 13px',
        variable: '--text-admin-base',
        className: 'text-admin-base text-admin-ink-secondary',
        sample: 'Caregivers appear here once an organization syncs.',
    },
    {
        label: 'emphasis · 14px',
        variable: '--text-admin-lg',
        className: 'text-admin-lg text-admin-ink font-medium',
        sample: 'Voice settings',
    },
    {
        label: 'page heading · 20px · -1.5% tracking (section-level)',
        variable: '--text-admin-xl',
        className:
            'text-admin-xl text-admin-ink font-semibold tracking-[-0.015em]',
        sample: 'Organizations',
    },
    {
        label: 'page display · 32px · 700 · -2% tracking (AdminPageHeader)',
        variable: 'text-[32px]',
        className:
            'text-admin-ink text-[32px] leading-[1] font-bold tracking-[-0.02em]',
        sample: 'Organizations',
    },
    {
        label: 'stat value · 24px · tabular',
        variable: '--text-admin-2xl',
        className:
            'text-admin-2xl text-admin-ink font-semibold admin-tabular tabular-nums',
        sample: '1,284',
    },
];

export type RadiusRow = {
    readonly label: string;
    readonly variable: string;
    readonly className: string;
    readonly value: string;
};

export const RADIUS_ROWS: ReadonlyArray<RadiusRow> = [
    {
        label: 'sm',
        variable: '--radius-admin-sm',
        className: 'rounded-admin-sm',
        value: '4px',
    },
    {
        label: 'md',
        variable: '--radius-admin-md',
        className: 'rounded-admin-md',
        value: '6px',
    },
    {
        label: 'lg',
        variable: '--radius-admin-lg',
        className: 'rounded-admin-lg',
        value: '8px',
    },
];

export type MotionRow = {
    readonly label: string;
    readonly variable: string;
    readonly value: string;
};

export const MOTION_ROWS: ReadonlyArray<MotionRow> = [
    { label: 'dur-fast', variable: '--admin-dur-fast', value: '120ms' },
    { label: 'dur-base', variable: '--admin-dur-base', value: '180ms' },
    { label: 'dur-slow', variable: '--admin-dur-slow', value: '240ms' },
    {
        label: 'ease-out',
        variable: '--admin-ease-out',
        value: 'cubic-bezier(0.16, 1, 0.3, 1)',
    },
    {
        label: 'ease-in',
        variable: '--admin-ease-in',
        value: 'cubic-bezier(0.4, 0, 1, 1)',
    },
    {
        label: 'ease-in-out',
        variable: '--admin-ease-in-out',
        value: 'cubic-bezier(0.4, 0, 0.2, 1)',
    },
];

/*
 * Reduced-motion-safe stagger. Returns the CSS-variable style + class
 * name that the admin-theme layer's `@keyframes admin-stagger-in`
 * consumes. The theme layer disables the animation under
 * `prefers-reduced-motion: reduce` so callers do not need to guard.
 * The step and max are locked to the round-4 contract: 24ms per row,
 * capped at 8 rows (192ms total) so no row waits perceptibly.
 */
const STAGGER_STEP_MS = 24;
const STAGGER_MAX = 8;

// Not a hook — just a helper. `staggerProps(i)` returns the class name
// + inline CSS variable the theme layer consumes; it does not read or
// write React state so it is safe to call inside `.map()` callbacks
// where the `useX` naming would trip the rules-of-hooks lint. Exported
// so unit tests can assert the clamp behavior without mounting the
// full showcase route.
export function staggerProps(index: number): {
    readonly className: string;
    readonly style: React.CSSProperties;
} {
    const clamped = Math.min(index, STAGGER_MAX);
    return {
        className: 'admin-stagger-item',
        style: {
            ['--admin-stagger-i' as string]: clamped,
            ['--admin-stagger-step' as string]: `${STAGGER_STEP_MS}ms`,
        },
    };
}

export function SwatchRow({
    swatch,
    index,
}: {
    readonly swatch: Swatch;
    readonly index: number;
}) {
    const stagger = staggerProps(index);
    const previewStyle: React.CSSProperties =
        swatch.textOn !== undefined
            ? { backgroundColor: swatch.textOn, color: swatch.value }
            : { backgroundColor: swatch.value };
    return (
        <div
            data-testid={`swatch-row-${swatch.name}`}
            className={cn(
                'border-admin-border flex items-center gap-3 border-b py-2 last:border-b-0',
                stagger.className,
            )}
            style={stagger.style}
        >
            <span
                aria-hidden="true"
                className={cn(
                    'border-admin-border rounded-admin-sm flex size-6 shrink-0 items-center justify-center border font-semibold',
                    swatch.textOn !== undefined && 'text-[11px]',
                )}
                style={previewStyle}
            >
                {swatch.textOn !== undefined ? 'Aa' : null}
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="text-admin-base text-admin-ink font-medium">
                    {swatch.name}
                    {swatch.note !== undefined && (
                        <span className="text-admin-sm text-admin-ink-muted ml-2 font-normal">
                            · {swatch.note}
                        </span>
                    )}
                </div>
                <code className="text-admin-xs text-admin-ink-muted admin-tabular font-mono">
                    {swatch.variable}
                </code>
            </div>
            <code className="text-admin-xs text-admin-ink-secondary admin-tabular font-mono">
                {swatch.value}
            </code>
        </div>
    );
}

export function SwatchColumn({
    title,
    swatches,
}: {
    readonly title: string;
    readonly swatches: ReadonlyArray<Swatch>;
}) {
    return (
        <AdminCard title={title}>
            {swatches.map((swatch, index) => (
                <SwatchRow
                    key={swatch.variable}
                    swatch={swatch}
                    index={index}
                />
            ))}
        </AdminCard>
    );
}

export function PhoebePurpleStory() {
    // Editorial poster: primary CTA panel + a full-scale ramp so the
    // Phoebe purple story reads without scrolling the swatch grid.
    // The poster panel proves the CTA/hover-strong pair on a real
    // surface; the ramp underneath prints every wash/soft/border/base
    // /strong stop plus the ink-on-purple pairing with its variable
    // name and hex so a reviewer can judge legibility in situ.
    return (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
            <div className="rounded-admin-lg border-admin-purple-border bg-admin-purple-wash flex flex-col justify-between gap-6 border p-6">
                <div className="flex flex-col gap-2">
                    <span className="text-admin-xs text-admin-purple-strong font-medium tracking-[0.06em] uppercase">
                        Phoebe purple · #5533ee
                    </span>
                    <h3 className="text-admin-ink text-[28px] leading-[1.15] font-bold tracking-[-0.02em] text-balance">
                        The signature colour of Phoebe admin.
                    </h3>
                    <p className="text-admin-base text-admin-ink-secondary max-w-md text-pretty">
                        Primary CTAs, focus rings, toggle on-states, active tab
                        underlines, the live status dot, and card-hover
                        hairlines all resolve to this hue. Surfaces stay quiet
                        at rest; interaction lights the brand up.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <AdminButton variant="primary">
                        <Plus />
                        Primary action
                    </AdminButton>
                    <AdminBadge tone="ink-primary" variant="solid">
                        Live
                    </AdminBadge>
                    <AdminStatusDot tone="purple" label="Live" />
                </div>
            </div>
            <div className="rounded-admin-lg border-admin-border bg-admin-surface flex flex-col overflow-hidden border">
                {PURPLE_SWATCHES.map((swatch, index) => (
                    <div
                        key={swatch.variable}
                        data-testid={`purple-story-row-${swatch.name}`}
                        className="border-admin-border flex items-center gap-3 border-b px-4 py-3 last:border-b-0"
                        style={{
                            ['--admin-stagger-i' as string]: Math.min(index, 8),
                        }}
                    >
                        <span
                            aria-hidden="true"
                            className="border-admin-border rounded-admin-sm flex size-10 shrink-0 items-center justify-center border text-[13px] font-semibold"
                            style={
                                swatch.textOn !== undefined
                                    ? {
                                          backgroundColor: swatch.textOn,
                                          color: swatch.value,
                                      }
                                    : { backgroundColor: swatch.value }
                            }
                        >
                            {swatch.textOn !== undefined ? 'Aa' : null}
                        </span>
                        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <div className="text-admin-base text-admin-ink font-medium">
                                {swatch.name}
                            </div>
                            <code className="text-admin-xs text-admin-ink-muted admin-tabular font-mono">
                                {swatch.variable}
                            </code>
                        </div>
                        <code className="text-admin-xs text-admin-ink-secondary admin-tabular font-mono">
                            {swatch.value}
                        </code>
                    </div>
                ))}
            </div>
        </div>
    );
}
